import {AfterViewInit, Component, ElementRef, HostListener, Inject, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router, RouterOutlet} from "@angular/router";
import {AuthenticationService} from "./_auth/authentication.service";
import {PermissionService} from "./_auth/permission.service";
import {AppRoutes} from "./enums/routes";
import {NavbarService} from "./x-components/navbar-base/navbar.service";
import {KeyboardService} from "./_services/keyboard.service";
import {WindowStateService} from "./_services/window-state.service";
import {CookieService} from "./_services/cookie.service";
import {A} from "@angular/cdk/keycodes";
import {LoadingIndicatorService} from "./_services/loading-indicator.service";
import {AlignmentService} from "./_services/alignment.service";
import {CachingService} from "./_services/caching.service";
import {App} from "./app.config";
import {CookieSettingsComponent} from "./x-components/cookie-settings/cookie-settings.component";
import {InfoMessageComponent} from "./x-components/info-message/info-message.component";
import {FullscreenLoaderComponent} from "./x-components/fullscreen-loader/fullscreen-loader.component";
import {AlertBannerComponent} from "./x-components/alert-banner/alert-banner.component";
import {HelpIntegrationComponent} from "./x-components/help-integration/help-integration.component";
import {NavbarOfficeComponent} from "./x-components/navbar-office/navbar-office.component";
import {ImpersonationBannerComponent} from "./x-components/impersonation-banner/impersonation-banner.component";
import {MigrationNoticeHostComponent} from "./_auth/migration/migration-notice-host.component";
import {SignInNoticeComponent} from "./_auth/notice/sign-in-notice.component";
import {ContextSwitcherComponent} from "./_auth/context/context-switcher.component";
import {isChromeFreePath} from "./_auth/chrome-free-routes";
import {CommonModule} from "@angular/common";
import {ContextExpiryTimerService} from "./_auth/context-expiry-timer.service";
import {GateRefreshService} from "./_auth/gate-refresh.service";

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    CookieSettingsComponent,
    InfoMessageComponent,
    FullscreenLoaderComponent,
    RouterOutlet,
    AlertBannerComponent,
    HelpIntegrationComponent,
    NavbarOfficeComponent,
    ImpersonationBannerComponent,
    ContextSwitcherComponent,
    MigrationNoticeHostComponent,
    SignInNoticeComponent
  ],
  templateUrl: './app.component.html'
})
export class AppComponent implements AfterViewInit {
  title = 'app';
  isAuthorized: boolean | null = null;
  /**
   * True for any session that renders inside its own minimal panel shell (Employee, Operator — #205) rather
   * than the admin navbar/dashboard. Named for its historical Employee-only meaning; now delegates to
   * {@link AuthenticationService.isPanelUser} so a standalone Operator session (which is NOT an Employee) does
   * not fall into the `@else` branch and render `app-navbar-office` — an admin chrome an Operator has no
   * permissions behind, and whose data calls would 403.
   */
  isEmployee: boolean = true;

  loaded: boolean = false;
  protected readonly A = A;
  protected readonly LoadingIndicatorService = LoadingIndicatorService;
  protected readonly Routes = AppRoutes;

  constructor(private app: App,
              public authenticationService: AuthenticationService, @Inject("BASE_URL") public baseUrl: string,
              public route: ActivatedRoute, public router: Router, public NavbarService: NavbarService,
              protected keyboardService: KeyboardService, private windowStateService: WindowStateService,
              private cookieService: CookieService, public AlignmentService: AlignmentService,
              private cachingService: CachingService, private permissionService: PermissionService,
              // Injected purely to start it (providedIn: 'root' is otherwise lazy) — arms the proactive
              // context-expiry timer for a session restored from storage at bootstrap AND one entered later.
              private contextExpiryTimerService: ContextExpiryTimerService,
              // Injected for the same reason and in the same way (#558): `providedIn: 'root'` is lazy, so the
              // live gate refresh only exists once something asks for it. Constructing it here opens the
              // gates hub (its server push is what makes a requirement assigned mid-session land in seconds),
              // arms the deadline alarm and the daily watchdog, and runs the first check — for a session
              // restored from storage at bootstrap as well as one signed in later.
              private gateRefreshService: GateRefreshService) {
    this.router.events.subscribe((x) => {
      if (x instanceof NavigationStart) {
        this.isEmployee = this.authenticationService.isPanelUser()
      }
    })
  }

  @ViewChild("relative") set relative(value: ElementRef | undefined) {
    this.AlignmentService.MainWindow = value?.nativeElement;
  }

  ngAfterViewInit(): void {
    this.loaded = true;
  }

  @HostListener('document:click', ['$event'])
  click(event: MouseEvent) {
    let element = event.target as HTMLElement;
    this.keyboardService.isOptionPressed = event.altKey;
    if (this.keyboardService.isOptionPressed && element.tagName.toLowerCase() == "a") {
      event.preventDefault();
      window.open(element.getAttribute("href")!, '_blank');
      this.keyboardService.isOptionPressed = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    this.keyboardService.pressedKey(event);
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEventUp(event: KeyboardEvent) {
    this.keyboardService.releasedKey(event);
  }

  @HostListener("window:blur", ["$event"])
  handleWindowBlur(event: FocusEvent) {
    this.keyboardService.resetKeys();
  }

  /**
   * True for a standalone {@link Roles.Operator} session, which gets its **own** shell branch, ahead of the
   * employee one (#205).
   *
   * An Operator is a panel user — so {@link isEmployee} is true for it as well — but it is NOT an employee: its
   * shell ({@link OperatorComponent}) draws a 56 px (`h-14`) top bar of its own, where the employee panel's is
   * 80 px on `lg`. Sharing the employee branch therefore offset everything laid out in normal flow by that
   * branch's `pt-10 lg:pt-20`, leaving a 24 px gap under the Operator's own bar.
   *
   * Read live rather than cached on `NavigationStart` like {@link isEmployee}: the branch must be right on the
   * very first render after a login redirect, before any further navigation happens.
   */
  IsOperator(): boolean {
    return this.permissionService.IsOperator();
  }

  IsSelfService(): boolean {
    return location.pathname.includes(AppRoutes.SelfServiceModule) ||
      location.pathname.includes(AppRoutes.MailingModule);
  }

  /**
   * True on the authentication routes, which render **without** any chrome regardless of whether a session
   * happens to be stored in this browser (`/login`, `/invite`, `/authentication/signing-in` — see
   * {@link CHROME_FREE_ROUTES} for why each is in the list).
   *
   * Keyed on the ROUTE, deliberately, and read live from `location.pathname` exactly as {@link IsSelfService}
   * does. `IsLoggedIn()` alone was the wrong question on these pages twice over: an invitee opening a forwarded
   * link on a shared machine was shown the previous user's name, company and navigation, and the handoff
   * landing rendered a shell whose bootstrap calls went out under a session the provider callback had already
   * superseded — 401ing, and taking the just-completed sign-in down with them.
   */
  IsChromeFree(): boolean {
    return isChromeFreePath(location.pathname);
  }

  IsLoggedIn(): boolean {
    return !!this.authenticationService.userValue?.jwt;
  }
}
